import { Article, Category, Author } from '@/types/article';
import { ArticleDomainSettings } from '@/types/domain';
import supabaseClient from './supabaseClient';

// Cache for commonly used data to reduce API calls
let articlesCache: Article[] | null = null;
let categoriesCache: Category[] | null = null;
let authorsCache: Author[] | null = null;

/**
 * Format database response into Article type
 */
const formatArticle = (data: any, domainSettings?: ArticleDomainSettings): Article => {
  return {
    id: data.id,
    title: domainSettings?.seo_title || data.title,
    slug: data.slug,
    excerpt: domainSettings?.custom_excerpt || data.excerpt || '',
    content: data.content,
    featuredImage: domainSettings?.featured_image || data.featured_image,
    publishedAt: data.published_at,
    readTime: data.read_time,
    status: data.status || 'published',
    createdAt: data.created_at || new Date().toISOString(),
    updatedAt: data.updated_at || new Date().toISOString(),
    author: {
      id: data.author?.id || '',
      name: data.author?.name || '',
      bio: data.author?.bio || '',
      avatar: data.author?.avatar || '',
      twitter: data.author?.twitter || '',
    },
    categories: data.categories || [],
    tags: data.tags || [], // Tags are already transformed in the data flattening step
    seo: {
      title: domainSettings?.seo_title || data.seo_title || data.title,
      description: domainSettings?.seo_description || data.seo_description || data.excerpt || '',
    }
  };
};

/**
 * Get articles from Supabase with domain support
 */
export const getArticles = async (params?: {
  page?: number;
  limit?: number;
  category?: string;
  tag?: string;
  domainId?: string;
}): Promise<{ articles: Article[]; total: number }> => {
  try {
    // Reset cache if fetching fresh data
    if (params) {
      articlesCache = null;
    }

    // Use cache if available
    if (!articlesCache) {
      let query = supabaseClient
        .from('articles')
        .select(`
          id,
          title,
          slug,
          excerpt,
          content,
          featured_image,
          published_at,
          read_time,
          seo_title,
          seo_description,
          author:article_authors(authors(*)),
          categories:article_categories(categories(*)),
          tags:article_tags(tags(*)),
          article_domain_mappings!inner(
            domain_id,
            status,
            published_at
          ),
          article_domain_settings!left(
            seo_title,
            seo_description,
            featured_image,
            custom_excerpt,
            is_featured,
            display_order
          )
        `)
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      // Apply domain filter if specified
      if (params?.domainId) {
        query = query
          .eq('article_domain_mappings.domain_id', params.domainId)
          .eq('article_domain_mappings.status', 'published');
      }

      // Apply category filter
      if (params?.category) {
        const { data: categoryData } = await supabaseClient
          .from('categories')
          .select('id')
          .eq('slug', params.category)
          .single();
          
        if (categoryData) {
          const articleIds = await supabaseClient
            .from('article_categories')
            .select('article_id')
            .eq('category_id', categoryData.id);
            
          if (articleIds.data && articleIds.data.length > 0) {
            const ids = articleIds.data.map(item => item.article_id);
            query = query.in('id', ids);
          }
        }
      }
      
      // Apply tag filter
      if (params?.tag) {
        const { data: tagData } = await supabaseClient
          .from('tags')
          .select('id')
          .eq('slug', params.tag)
          .single();
          
        if (tagData) {
          const articleIds = await supabaseClient
            .from('article_tags')
            .select('article_id')
            .eq('tag_id', tagData.id);
            
          if (articleIds.data && articleIds.data.length > 0) {
            const ids = articleIds.data.map(item => item.article_id);
            query = query.in('id', ids);
          }
        }
      }

      // Apply pagination if specified
      if (params?.page !== undefined && params?.limit !== undefined) {
        const start = (params.page - 1) * params.limit;
        const end = start + params.limit - 1;
        query = query.range(start, end);
      }

      // Execute query
      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Process and transform data
      articlesCache = data.map((item: any) => {
        // Flatten nested structures returned by Supabase
        const article: any = {
          ...item,
          author: item.author?.[0]?.authors || {},
          categories: item.categories?.map((c: any) => c.categories) || [],
          tags: item.tags?.map((t: any) => {
            const tagData = t.tags || t;
            if (!tagData?.name) return null;
            return {
              id: tagData.id || crypto.randomUUID(),
              name: tagData.name,
              slug: tagData.slug || tagData.name.toLowerCase().replace(/\s+/g, '-')
            };
          }).filter(Boolean) || []
        };
        
        // Get domain settings for the specified domain if available
        const domainSettings = params?.domainId 
          ? item.article_domain_settings?.find((s: ArticleDomainSettings) => s.domain_id === params.domainId)
          : undefined;
        
        return formatArticle(article, domainSettings);
      });
    }

    return {
      articles: articlesCache || [],
      total: params?.page !== undefined ? await getTotalArticlesCount(params) : articlesCache?.length || 0
    };
  } catch (error) {
    console.error('Error fetching articles:', error);
    return { articles: [], total: 0 };
  }
};

/**
 * Get total article count for pagination with domain support
 */
const getTotalArticlesCount = async (params?: {
  category?: string;
  tag?: string;
  domainId?: string;
}): Promise<number> => {
  try {
    let query = supabaseClient
      .from('articles')
      .select('id', { count: 'exact' })
      .eq('status', 'published');

    // Apply domain filter if specified
    if (params?.domainId) {
      query = query
        .eq('article_domain_mappings.domain_id', params.domainId)
        .eq('article_domain_mappings.status', 'published');
    }

    // Apply filters if needed
    if (params?.category) {
      const { data: categoryData } = await supabaseClient
        .from('categories')
        .select('id')
        .eq('slug', params.category)
        .single();
        
      if (categoryData) {
        const articleIds = await supabaseClient
          .from('article_categories')
          .select('article_id')
          .eq('category_id', categoryData.id);
          
        if (articleIds.data && articleIds.data.length > 0) {
          const ids = articleIds.data.map(item => item.article_id);
          query = query.in('id', ids);
        }
      }
    }
    
    if (params?.tag) {
      const { data: tagData } = await supabaseClient
        .from('tags')
        .select('id')
        .eq('slug', params.tag)
        .single();
        
      if (tagData) {
        const articleIds = await supabaseClient
          .from('article_tags')
          .select('article_id')
          .eq('tag_id', tagData.id);
          
        if (articleIds.data && articleIds.data.length > 0) {
          const ids = articleIds.data.map(item => item.article_id);
          query = query.in('id', ids);
        }
      }
    }

    const { count } = await query;
    return count || 0;
  } catch (error) {
    console.error('Error getting total article count:', error);
    return 0;
  }
};

/**
 * Get a specific article by its slug with domain support
 */
export const getArticleBySlug = async (slug?: string, domainId?: string): Promise<Article | null> => {
  if (!slug) return null;
  
  try {
    let query = supabaseClient
      .from('articles')
      .select(`
        id,
        title,
        slug,
        excerpt,
        content,
        featured_image,
        published_at,
        read_time,
        seo_title,
        seo_description,
        author:article_authors(authors(*)),
        categories:article_categories(categories(*)),
        tags:article_tags(tags(*)),
        article_domain_mappings!inner(
          domain_id,
          status,
          published_at
        ),
        article_domain_settings!left(
          id,
          article_id,
          domain_id,
          seo_title,
          seo_description,
          featured_image,
          custom_excerpt,
          is_featured,
          display_order,
          created_at,
          updated_at
        )
      `)
      .eq('slug', slug)
      .eq('status', 'published');

    // Apply domain filter if specified
    if (domainId) {
      query = query
        .eq('article_domain_mappings.domain_id', domainId)
        .eq('article_domain_mappings.status', 'published');
    }

    const { data: articleData, error: articleError } = await query.single();

    if (articleError) {
      throw articleError;
    }

    if (!articleData) {
      return null;
    }

    // Flatten nested structures
    const article: any = {
      ...articleData,
      author: articleData.author?.[0]?.authors || {},
      categories: articleData.categories?.map((c: any) => c.categories) || [],
      tags: articleData.tags?.map((t: any) => t.tags?.name) || []
    };
    
    // Get domain settings for the specified domain if available
    const domainSettings = domainId 
      ? articleData.article_domain_settings?.find((s: any) => s.domain_id === domainId) as ArticleDomainSettings | undefined
      : undefined;
    
    return formatArticle(article, domainSettings);
  } catch (error) {
    console.error('Error fetching article by slug:', error);
    return null;
  }
};

/**
 * Get articles filtered by category
 */
export const getArticlesByCategory = async (categorySlug: string): Promise<Article[]> => {
  const { articles } = await getArticles({ category: categorySlug });
  return articles;
};

/**
 * Get all categories from Supabase
 */
export const getAllCategories = async (): Promise<Category[]> => {
  try {
    if (!categoriesCache) {
      const { data, error } = await supabaseClient
        .from('categories')
        .select('*')
        .order('name');

      if (error) {
        throw error;
      }

      categoriesCache = data.map((category: any) => ({
        id: category.id,
        name: category.name,
        slug: category.slug,
        description: category.description
      }));
    }
    
    return categoriesCache || [];
  } catch (error) {
    console.error('Error fetching all categories:', error);
    return [];
  }
};

/**
 * Get a specific category by its slug
 */
export const getCategoryBySlug = async (slug: string): Promise<Category | undefined> => {
  try {
    const { data, error } = await supabaseClient
      .from('categories')
      .select('*')
      .eq('slug', slug)
      .single();

    if (error) {
      throw error;
    }

    return data ? {
      id: data.id,
      name: data.name,
      slug: data.slug,
      description: data.description
    } : undefined;
  } catch (error) {
    console.error('Error fetching category by slug:', error);
    return undefined;
  }
};

/**
 * Get all authors from Supabase
 */
export const getAllAuthors = async (): Promise<Author[]> => {
  try {
    if (!authorsCache) {
      const { data, error } = await supabaseClient
        .from('authors')
        .select('*')
        .order('name');

      if (error) {
        throw error;
      }

      authorsCache = data.map((author: any) => ({
        id: author.id,
        name: author.name,
        bio: author.bio,
        avatar: author.avatar,
        twitter: author.twitter
      }));
    }
    
    return authorsCache || [];
  } catch (error) {
    console.error('Error fetching all authors:', error);
    return [];
  }
};

/**
 * Get a specific author by their ID
 */
export const getAuthorById = async (id: string): Promise<Author | undefined> => {
  try {
    const { data, error } = await supabaseClient
      .from('authors')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      throw error;
    }

    return data ? {
      id: data.id,
      name: data.name,
      bio: data.bio,
      avatar: data.avatar,
      twitter: data.twitter
    } : undefined;
  } catch (error) {
    console.error('Error fetching author by id:', error);
    return undefined;
  }
}; 